import { useDispatch, useSelector } from "react-redux";
import { atcPopupupdateDate, atcPopupupdateQty } from "~/features/contracts/redux/actions";
import { type RootState } from "~/app/store";
import { Trans, useTranslation } from "react-i18next";
import { contractIsDayDisabled, getContractYears } from "~/features/contracts/utils/contractDateUtils";
import { type NetPriceData } from "~/domains/pricing/types/PriceData";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import { Stack } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { AtcMainAreaLayout } from "~/features/addToCart/components/AddToCartProcess/shell/AtcContentLayout";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { Parameter } from "~/common/components/ParameterV2";
import { GPSDParam } from "~/domains/product/components/ProductParamsV3";
import { MUIDateInput } from "~/common/components/MUIDateInput";
import {
  ContractLineItemStatus,
  CONTRACT_LINE_ITEM_STATUS_CODES,
} from "~/features/contracts/models/contractLineItemStatus";

export interface PopupData {
  quantity: number;
  requestedDeliveryDate: Date;
}

const MESSAGE_MAP = {
  [ContractLineItemStatus.Active]: "contractDetails.atc.remainingQuantity",
  [ContractLineItemStatus.Waiting]: "contractDetails.atc.waiting",
  [ContractLineItemStatus.Completed]: "contractDetails.atc.completed",
  [ContractLineItemStatus.Cancelled]: "contractDetails.atc.cancelled",
};

export interface ContractVersionContentsProps {
  productDescriptor: ProductDescriptor;
  packageSize: number;
  packCode: string;

  contractDetails: ContractDetailsResponse;
}

const ContractVersionContents = ({
  productDescriptor,
  packageSize,
  packCode,
  contractDetails,
}: ContractVersionContentsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const selectedDate = useSelector((state: RootState) => state.contracts.popupData.selectedDate);
  const selectedQty = useSelector((state: RootState) => state.contracts.popupData.selectedQty);

  const isDayDisabled = (date: Date) => {
    return contractIsDayDisabled(contractDetails, date);
  };

  const quantity = selectedQty || packageSize;
  const requestedDeliveryDate = selectedDate;

  const isPackageSizeInvalid = !quantity || !Number.isInteger(quantity / packageSize);
  const isDateInvalid = !requestedDeliveryDate;

  const status = contractDetails.status;

  const willSplit = quantity > contractDetails.openQuantity;
  const isActive =
    CONTRACT_LINE_ITEM_STATUS_CODES[status as keyof typeof CONTRACT_LINE_ITEM_STATUS_CODES] ===
    ContractLineItemStatus.Active;

  const { minYear, maxYear } = getContractYears(contractDetails);

  const price = contractDetails.nettoPrice;

  const priceUnit = Number(contractDetails.priceUnit) || 1;

  const netPrice: NetPriceData = {
    ...price,
    unit: priceUnit,
    baseValue: price.value, // With no surcharge, base value === value
    surcharge: 0, // Can contracts have surcharge?
    isSpecial: false, // Can contracts have special price?
    isSubscription: !productDescriptor.base.quantityVisible,
  };

  const packagingSlot = (
    <Stack gap={2}>
      <MUIDateInput
        label={t("addToCart.contract.rdd")}
        dataTestId={"add-to-cart-date-selector-rdd"}
        date={requestedDeliveryDate ?? null}
        isInvalid={isDateInvalid}
        onChange={(newDate) => dispatch(atcPopupupdateDate(newDate))}
        isDayDisabled={isDayDisabled}
        maxDate={maxYear !== undefined ? new Date(maxYear, 11, 31) : undefined}
        minDate={minYear !== undefined ? new Date(minYear, 0, 1) : undefined}
        showErrorIcon={isDateInvalid && true}
        helperLabel={isDateInvalid && t("addToCartPopup.date.error")}
      />

      {isPackageSizeInvalid && <Alert severity="error">{t("addToCartPopup.mismatchedQty")}</Alert>}

      {(!isActive || willSplit) && (
        <Alert severity="info">
          <Trans
            i18nKey={
              MESSAGE_MAP[
                CONTRACT_LINE_ITEM_STATUS_CODES[
                  status as keyof typeof CONTRACT_LINE_ITEM_STATUS_CODES
                ] as ContractLineItemStatus
              ]
            }
            values={{ openQuantity: contractDetails.openQuantity }}
          >
            Your remaining quantity for this contract is <b>{contractDetails.openQuantity}.</b>
          </Trans>

          <p>{t(isActive ? "contractDetails.atc.additionalQuantity" : "contractDetails.atc.regularConditions")}</p>
        </Alert>
      )}
    </Stack>
  );

  const pricingSlot = (
    <Stack gap={2}>
      <ContractPriceBlock
        netPrice={netPrice}
        descriptor={productDescriptor}
        packCode={packCode}
        packSize={packageSize}
      />

      <QuantityInput
        variant="compact"
        onValueChange={(newQty) => dispatch(atcPopupupdateQty(newQty))}
        value={quantity}
        step={packageSize}
        error={!!isPackageSizeInvalid}
      />
    </Stack>
  );

  return <AtcMainAreaLayout pricingSlot={pricingSlot} packagingSlot={packagingSlot} />;
};

export default ContractVersionContents;

interface ContractriceBlockProps {
  netPrice: NetPriceData;
  descriptor: ProductDescriptor;
  packCode: string;
  packSize: number;
}

const ContractPriceBlock = ({ netPrice, descriptor, packCode, packSize }: ContractriceBlockProps) => {
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <PriceElements.Price price={netPrice} variant="headlineMedium" sx={{ fontWeight: "bold" }} />
        <PriceElements.NetPriceLabel price={netPrice} />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        sx={({ breakpoints }) => ({ [breakpoints.down("tablet")]: { flexDirection: "column" } })}
      >
        <Parameter
          label={t("addToCart.contract.quantity")}
          value={t("addToCartPopup.packSize", { amount: packSize, code: packCode })}
        />
        <GPSDParam product={descriptor.base} />
      </Stack>
    </Stack>
  );
};
